import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Link2, Copy, RefreshCw, X, CreditCard, Loader2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { money } from "../lib/helpers";
import { NativeSelect } from "./Common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

const STATUS = { open: "Waiting for payment", paid: "Paid", cancelled: "Cancelled" };
const payUrl = (l) => `${window.location.origin}/pay/${l.token}`;

function LinkRow({ l, onChange }) {
  const [qr, setQr] = useState(false);
  const copy = () => { navigator.clipboard.writeText(payUrl(l)); toast.success("Link copied"); };
  const check = () => api.post(`/sumup/links/${l.id}/check`).then((r) => { toast.success(STATUS[r.data.status]); onChange(); }).catch((e) => toast.error(errMsg(e)));
  const cancel = () => api.delete(`/sumup/links/${l.id}`).then(onChange).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="border-t py-2 text-sm" data-testid={`sumup-link-${l.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div><div className="font-medium">{money(l.amount, l.currency)}</div>
          <div className={`text-xs ${l.status === "paid" ? "text-emerald-700" : "text-muted-foreground"}`} data-testid={`sumup-link-status-${l.id}`}>{STATUS[l.status]}{l.emailed ? " · emailed" : ""}</div></div>
        {l.status === "open" && <div className="flex gap-1">
          <Button size="sm" variant="ghost" data-testid={`sumup-link-qr-${l.id}`} onClick={() => setQr(!qr)}>QR</Button>
          <Button size="icon" variant="ghost" data-testid={`sumup-link-copy-${l.id}`} onClick={copy}><Copy className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" data-testid={`sumup-link-check-${l.id}`} onClick={check}><RefreshCw className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" data-testid={`sumup-link-cancel-${l.id}`} onClick={cancel}><X className="h-4 w-4" /></Button>
        </div>}
      </div>
      {qr && l.status === "open" && <div className="mt-2 flex flex-col items-center gap-1" data-testid={`sumup-link-qr-code-${l.id}`}>
        <div className="rounded-xl bg-white p-3 border"><QRCodeSVG value={payUrl(l)} size={180} /></div>
        <p className="text-xs text-muted-foreground">The patient scans this with their phone to pay (card, Apple Pay or Google Pay)</p></div>}
    </div>
  );
}

function LinkTab({ appointment, balance, onDone }) {
  const [links, setLinks] = useState([]);
  const [amount, setAmount] = useState(balance);
  const [email, setEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/sumup/links", { params: { appointment_id: appointment.id } }).then((r) => setLinks(r.data)), [appointment.id]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post("/sumup/links", { appointment_id: appointment.id, amount: Number(amount), send_email: email && !!appointment.client_email });
      navigator.clipboard?.writeText(r.data.url).catch(() => {});
      toast.success(r.data.emailed ? "Payment link emailed and copied" : "Payment link copied"); load(); onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">The patient pays on SumUp's secure page. The payment is added here automatically once SumUp confirms it.</p>
      <div className="flex items-center gap-2"><span className="text-sm">{appointment.currency}</span>
        <Input type="number" min={0.01} step="0.01" className="w-32" data-testid="sumup-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
      {appointment.client_email && <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" data-testid="sumup-email-checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} className="h-4 w-4 accent-[hsl(var(--primary))]" />
        Email the link to {appointment.client_email}</label>}
      <Button className="rounded-full" disabled={busy || !(Number(amount) > 0)} data-testid="sumup-create-link-btn" onClick={create}><Link2 className="h-4 w-4 mr-1.5" />Create link</Button>
      {links.length > 0 && <div data-testid="sumup-links-list">{links.map((l) => <LinkRow key={l.id} l={l} onChange={load} />)}</div>}
    </div>
  );
}

const RP_TEXT = { pending: "Waiting for the card on the reader…", paid: "Paid", failed: "Payment failed or declined", cancelled: "Cancelled" };

function ReaderTab({ appointment, balance, ready, onDone }) {
  const [readers, setReaders] = useState(null);
  const [reader, setReader] = useState("");
  const [amount, setAmount] = useState(balance);
  const [rp, setRp] = useState(null);
  useEffect(() => {
    if (!ready) return;
    api.get("/sumup/readers").then((r) => { setReaders(r.data); setReader(r.data[0]?.id || ""); }).catch((e) => { setReaders([]); toast.error(errMsg(e)); });
  }, [ready]);
  useEffect(() => {
    if (rp?.status !== "pending") { if (rp?.status === "paid") onDone(); return; }
    const timer = setTimeout(() => api.get(`/sumup/reader-pay/${rp.id}`).then((r) => setRp(r.data)).catch(() => {}), 3000);
    return () => clearTimeout(timer);
  }, [rp]); // eslint-disable-line react-hooks/exhaustive-deps
  const start = () => api.post("/sumup/reader-pay", { appointment_id: appointment.id, reader_id: reader, amount: Number(amount) })
    .then((r) => setRp(r.data)).catch((e) => toast.error(errMsg(e)));
  const cancel = () => api.post(`/sumup/reader-pay/${rp.id}/cancel`).then(() => setRp({ ...rp, status: "cancelled" })).catch((e) => toast.error(errMsg(e)));
  if (!ready) return <p className="text-sm text-muted-foreground" data-testid="sumup-reader-not-ready">To use a SumUp Solo card reader, add your SumUp affiliate key and pair the reader in Settings → Payments.</p>;
  if (readers && !readers.length) return <p className="text-sm text-muted-foreground" data-testid="sumup-no-readers">No card reader is paired yet. Pair one in Settings → Payments.</p>;
  return (
    <div className="space-y-3">
      <NativeSelect data-testid="sumup-reader-select" value={reader} onChange={(e) => setReader(e.target.value)}>
        {(readers || []).map((r) => <option key={r.id} value={r.id}>{r.name || r.id}</option>)}
      </NativeSelect>
      <div className="flex items-center gap-2"><span className="text-sm">{appointment.currency}</span>
        <Input type="number" min={0.01} step="0.01" className="w-32" data-testid="sumup-reader-amount" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
      {rp ? <div className="flex items-center justify-between rounded-[var(--radius)] border p-3 text-sm" data-testid="sumup-reader-status">
        <span className="flex items-center gap-2">{rp.status === "pending" && <Loader2 className="h-4 w-4 animate-spin" />}{RP_TEXT[rp.status]}</span>
        {rp.status === "pending" ? <Button size="sm" variant="outline" data-testid="sumup-reader-cancel" onClick={cancel}>Cancel</Button>
          : <Button size="sm" variant="ghost" data-testid="sumup-reader-again" onClick={() => setRp(null)}>New payment</Button>}
      </div> : <Button className="rounded-full" disabled={!reader || !(Number(amount) > 0)} data-testid="sumup-reader-send" onClick={start}><CreditCard className="h-4 w-4 mr-1.5" />Send to reader</Button>}
    </div>
  );
}

export function SumUpDialog({ open, onOpenChange, appointment, balance, onPaid }) {
  const [st, setSt] = useState(null);
  useEffect(() => { if (open) api.get("/sumup/status").then((r) => setSt(r.data)); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) onPaid(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="sumup-dialog">
        <DialogHeader><DialogTitle>Take payment with SumUp</DialogTitle></DialogHeader>
        {st && !st.configured && <p className="rounded-[var(--radius)] border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-900" data-testid="sumup-not-ready">
          SumUp isn't connected yet. The clinic owner can connect it in Settings → Payments.</p>}
        {st?.configured && <Tabs defaultValue="link">
          <TabsList className="mb-3"><TabsTrigger value="link" data-testid="sumup-tab-link">Payment link / QR</TabsTrigger><TabsTrigger value="reader" data-testid="sumup-tab-reader">Card reader</TabsTrigger></TabsList>
          <TabsContent value="link"><LinkTab appointment={appointment} balance={balance} onDone={onPaid} /></TabsContent>
          <TabsContent value="reader"><ReaderTab appointment={appointment} balance={balance} ready={st.reader} onDone={onPaid} /></TabsContent>
        </Tabs>}
      </DialogContent>
    </Dialog>
  );
}
